import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useI18n } from "../lib/i18n";
import { useAuth, API } from "../lib/auth";
import { Loader2, Sparkles } from "lucide-react";

export default function RegisterPage() {
  const { t, lang } = useI18n();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const initial = sp.get("role") === "employer" ? "employer" : "candidate";
  const [tab, setTab] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [cand, setCand] = useState({ name: "", email: "", phone: "", password: "", title: "", company: "" });
  const [emp, setEmp] = useState({ company_name: "", contact_name: "", email: "", phone: "", password: "", sector: "", recruitment_needs: "" });
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    // Prefill candidate form from sessionStorage if user came through the landing CV matcher
    try {
      const raw = sessionStorage.getItem("motravay_prefill");
      if (raw) {
        const p = JSON.parse(raw);
        const toStr = (v) => Array.isArray(v) ? v.join(", ") : (v == null ? "" : String(v));
        setCand((c) => ({
          ...c,
          name: toStr(p.name) || c.name,
          email: toStr(p.email) || c.email,
          phone: toStr(p.phone) || c.phone,
          title: toStr(p.title) || c.title,
          company: toStr(p.company) || c.company,
        }));
        setPrefilled(true);
      }
    } catch (_) { /* ignore */ }
  }, []);

  const submitCand = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { title, company, ...payload } = cand;
      const r = await axios.post(`${API}/auth/register/candidate`, payload);
      setSession(r.data.token, r.data.user);
      // Best-effort: persist parsed title/company onto the new profile
      const patch = {};
      if (title) patch.title = title;
      if (company) patch.work_history = `${company}${title ? ` — ${title}` : ""}`;
      if (Object.keys(patch).length > 0) {
        try {
          await axios.patch(`${API}/candidate/profile`, patch, { headers: { Authorization: `Bearer ${r.data.token}` } });
        } catch (_) { /* non-blocking */ }
      }
      sessionStorage.removeItem("motravay_prefill");
      toast.success(lang === "fr" ? "Compte créé !" : "Account created!");
      navigate("/candidate");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setLoading(false);
  };
  const submitEmp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/register/employer`, emp);
      setSession(r.data.token, r.data.user);
      toast.success(lang === "fr" ? "Compte créé — choisissez un plan" : "Account created — pick a plan");
      navigate("/register/employer/plans");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Card className="p-8" data-testid="register-card">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-2">{t("auth.signupTitle")}</h1>
        <p className="text-sm text-slate-500 mb-6">Motravay</p>
        {prefilled && tab === "candidate" && (
          <div data-testid="register-prefill-banner" className="mb-5 rounded-xl bg-[#1A8FE3]/8 border border-[#1A8FE3]/20 px-4 py-3 flex gap-2 items-start">
            <Sparkles className="h-4 w-4 text-[#1A8FE3] mt-0.5 shrink-0" />
            <p className="text-xs text-slate-700 leading-relaxed">
              {lang === "fr"
                ? "Nous avons pré-rempli votre formulaire à partir de votre CV. Créer un compte vous permettra de postuler facilement et de recevoir des recommandations d'emploi personnalisées basées sur votre profil."
                : "We pre-filled your form from your CV. Creating an account enables you to apply easily to multiple roles and get smart job recommendations based on your profile."}
            </p>
          </div>
        )}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 mb-6 w-full">
            <TabsTrigger value="candidate" data-testid="tab-candidate">{t("auth.candidate")}</TabsTrigger>
            <TabsTrigger value="employer" data-testid="tab-employer">{t("auth.employer")}</TabsTrigger>
          </TabsList>
          <TabsContent value="candidate">
            <form onSubmit={submitCand} className="space-y-4">
              <div><Label>{t("auth.name")}</Label><Input data-testid="reg-cand-name" required value={cand.name} onChange={(e) => setCand({ ...cand, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="reg-cand-email" type="email" required value={cand.email} onChange={(e) => setCand({ ...cand, email: e.target.value })} /></div>
              <div><Label>{lang === "fr" ? "Téléphone" : "Phone"}</Label><Input data-testid="reg-cand-phone" value={cand.phone} onChange={(e) => setCand({ ...cand, phone: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{lang === "fr" ? "Dernier poste" : "Latest job title"}</Label><Input data-testid="reg-cand-title" value={cand.title} onChange={(e) => setCand({ ...cand, title: e.target.value })} /></div>
                <div><Label>{lang === "fr" ? "Entreprise actuelle" : "Current company"}</Label><Input data-testid="reg-cand-company" value={cand.company} onChange={(e) => setCand({ ...cand, company: e.target.value })} /></div>
              </div>
              <div><Label>{t("auth.password")}</Label><Input data-testid="reg-cand-password" type="password" required value={cand.password} onChange={(e) => setCand({ ...cand, password: e.target.value })} /></div>
              <Button type="submit" disabled={loading} data-testid="reg-cand-submit" className="w-full bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.signup")}
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="employer">
            <form onSubmit={submitEmp} className="space-y-4">
              <div><Label>{lang === "fr" ? "Nom de l'entreprise *" : "Business Name *"}</Label><Input data-testid="reg-emp-company" required value={emp.company_name} onChange={(e) => setEmp({ ...emp, company_name: e.target.value })} /></div>
              <div><Label>{lang === "fr" ? "Nom du contact *" : "Contact Name *"}</Label><Input data-testid="reg-emp-contact" required value={emp.contact_name} onChange={(e) => setEmp({ ...emp, contact_name: e.target.value })} /></div>
              <div><Label>{lang === "fr" ? "Email professionnel *" : "Business Email *"}</Label><Input data-testid="reg-emp-email" type="email" required value={emp.email} onChange={(e) => setEmp({ ...emp, email: e.target.value })} /></div>
              <div><Label>{lang === "fr" ? "Téléphone *" : "Phone Number *"}</Label><Input data-testid="reg-emp-phone" type="tel" required value={emp.phone} onChange={(e) => setEmp({ ...emp, phone: e.target.value })} /></div>
              <div>
                <Label>{lang === "fr" ? "Secteur d'activité" : "Business Sector"}</Label>
                <Input data-testid="reg-emp-sector" value={emp.sector} onChange={(e) => setEmp({ ...emp, sector: e.target.value })} placeholder={lang === "fr" ? "Ex. Hôtellerie, IT, Finance…" : "e.g. Hospitality, IT, Finance…"} />
              </div>
              <div>
                <Label>{lang === "fr" ? "Besoins en recrutement *" : "Recruitment Needs *"}</Label>
                <textarea
                  data-testid="reg-emp-needs"
                  required
                  rows={4}
                  value={emp.recruitment_needs}
                  onChange={(e) => setEmp({ ...emp, recruitment_needs: e.target.value })}
                  placeholder={lang === "fr"
                    ? "Parlez-nous des postes à pourvoir, du nombre d'employés recherchés et de vos défis actuels de recrutement."
                    : "Tell us about the positions you need to fill, the number of employees you are looking for and any recruitment challenges you currently face."}
                  className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1A8FE3]/30 focus:border-[#1A8FE3]"
                />
              </div>
              <div><Label>{t("auth.password")} *</Label><Input data-testid="reg-emp-password" type="password" required value={emp.password} onChange={(e) => setEmp({ ...emp, password: e.target.value })} /></div>
              <Button type="submit" disabled={loading} data-testid="reg-emp-submit" className="w-full bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.signup")}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
        <p className="text-sm text-slate-500 text-center mt-5">
          {t("auth.haveAccount")} <Link to="/login" className="text-[#1A8FE3] font-medium">{t("auth.login")}</Link>
        </p>
      </Card>
    </div>
  );
}
